import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ConfiguratorRoomDto } from '@/services/configurator';
import { BuyerProjectDeadline } from '@/types';
import { isDeadlineInPast } from '@/utils/date';
import { expandCategoryIdsToIncludeDescendants } from '@/utils/categories';
import { IState } from '@/state/IState';

import type { RoomGroup } from '../OptionsPage';
import type { OptionsFilter } from '../useOptionsFilter';
import { getGroupStatus } from './groupStatus';

/**
 * Flatten rooms into (room, group) pairs, keeping only the groups that pass the
 * active filter (status, deadline, room, category) and aren't past their deadline
 * when expired groups are hidden.
 *
 * @param matchableCategoryIds the selected categories expanded to include every
 *   descendant id (see `expandCategoryIdsToIncludeDescendants`); empty when no
 *   category filter is active.
 */
function filterRoomGroups(
    rooms: ConfiguratorRoomDto[],
    deadlines: BuyerProjectDeadline[],
    filter: OptionsFilter,
    matchableCategoryIds: Set<number>,
    isShowroom: boolean
): RoomGroup[] {
    const out: RoomGroup[] = [];
    for (const room of rooms) {
        for (const group of room.groups) {
            const status = getGroupStatus(group, isShowroom);
            if (!filter.statuses.includes(status)) continue;
            if (filter.deadline && filter.deadline !== group.deadlineId) continue;
            // Room and tag selections combine with OR: a group matches if it's in a selected
            // room or carries a selected tag. Inactive when neither is selected.
            if (filter.roomIds.length > 0 || filter.tagIds.length > 0) {
                const roomMatch = filter.roomIds.includes(room.roomId);
                const tagMatch = group.tags.some((t) => filter.tagIds.includes(t.id));
                if (!roomMatch && !tagMatch) continue;
            }
            if (filter.categories.length > 0) {
                const hasMatchingCategory = group.options.some((o) => {
                    const optCategoryId = o.product?.itemCategoryId;
                    return optCategoryId != null && matchableCategoryIds.has(optCategoryId);
                });
                if (!hasMatchingCategory) continue;
            }
            const dl = deadlines.find((d) => d.id === group.deadlineId);
            if (!dl) continue;
            // In the showroom deadlines are irrelevant, so expired deadlines are never hidden.
            // Submitted ('Sent') groups are exempt: a buyer signs before the deadline, so a
            // signed group is inevitably past-deadline once elapsed. Hiding it would make the
            // "Show submitted items" filter surface nothing whenever "Show expired items" is off.
            if (!isShowroom && !filter.showExpired && status !== 'Sent' && isDeadlineInPast(dl.deadline))
                continue;
            out.push({ room, group });
        }
    }
    return out;
}

/**
 * Resolves the visible (room, group) pairs for the options page: builds the
 * category hierarchy from the global item categories, then applies the active
 * filter. Returns `null` while rooms haven't loaded yet.
 */
export function useFilteredGroups(
    rooms: ConfiguratorRoomDto[] | null,
    deadlines: BuyerProjectDeadline[],
    filter: OptionsFilter,
    isShowroom = false
): RoomGroup[] | null {
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);
    const matchableCategoryIds = useMemo(
        () => new Set(expandCategoryIdsToIncludeDescendants(filter.categories, itemCategories)),
        [filter.categories, itemCategories]
    );

    return useMemo(() => {
        if (!rooms) return null;
        return filterRoomGroups(rooms, deadlines, filter, matchableCategoryIds, isShowroom);
    }, [rooms, deadlines, filter, matchableCategoryIds, isShowroom]);
}
